/* redesign-predictive.js — the search dropdown for the header, the mobile menu
   and the results page: every [data-rd-search] root on the page.

   WHY THIS DOES NOT USE SHOPIFY'S PREDICTIVE SEARCH FOR PRODUCTS.
   /search/suggest.json takes 650-850ms on this store, measured repeatedly over
   several queries. No debounce setting hides that — you finish typing, then you
   wait, which is exactly what Sam reported against Searchanise's panel. It also
   has no sort parameter, so it answers in relevance order with nothing to say
   about how well anything sells, and it carries no review data.

   The catalogue is 73 products. At that size the honest fix is to ship it:
   templates/collection.rank.liquid prints every product once, in best-selling
   order, with the fields a row needs, and this matches against it in memory.
   One fetch per tab — started on focus, before the first keystroke — and then
   every keystroke is instant, sorted by sales, with review counts attached.

   Collections come from the same file. Pages and articles do not: there are
   hundreds and they are the least urgent row, so they still come from the API
   and are APPENDED when they arrive, underneath everything already drawn, so
   nothing the reader is looking at moves.

   If the index has not arrived yet the panel falls back to the API for
   everything, which is what it used to do — slower, never broken.

   SEARCHANISE IS STILL ON THE PAGE. Its script is injected by a ScriptTag and a
   web pixel the app installed, not by this theme, so it cannot be removed from
   here — it has to be turned off in the admin. Until it is, it claims any
   input[name="q"] and hijacks the submit to its own results page. Taking the
   field back needs all three of: renaming the input, intercepting submit and
   Enter at document level in the CAPTURE phase, and re-asserting the rename for
   a few seconds because the pixel loads async and rescans.

   Accessibility: the input is a combobox, the panel a listbox. Up/Down move
   through results, Enter opens the active one, Escape closes and returns focus
   to the field. */

(function () {
  'use strict';

  var LIMIT = 4;       /* products shown */
  var DOCS = 3;        /* categories, and pages */
  var MIN = 2;

  /* ------------------------------------------------ the index */

  var INDEX_URL = '/collections/all?sort_by=best-selling&view=rank';
  /* KEYED BY CURRENCY (docs/MULTI-CURRENCY.md §4.2). The index carries
     prices, and a session can change currency part-way — the footer selector
     reloads the page in the new one — so an index cached in one currency must
     never quote it to a shopper now browsing in another. Keys for any other
     currency, and the unkeyed original, are dropped on the way in. */
  var INDEX_BASE = 'rd-search-index-v1';
  var INDEX_KEY = INDEX_BASE + ':' + ((window.rdMoney && window.rdMoney.currency) ||
    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '');
  try {
    for (var ik = sessionStorage.length - 1; ik >= 0; ik--) {
      var stale = sessionStorage.key(ik);
      if (stale && stale !== INDEX_KEY &&
          (stale === INDEX_BASE || stale.indexOf(INDEX_BASE + ':') === 0)) sessionStorage.removeItem(stale);
    }
  } catch (e) { /* storage blocked: nothing was cached to drop */ }
  var index = null, indexPending = null;

  /* NO `Accept: application/json` HERE. A collection URL content-negotiates:
     ask for JSON and Shopify looks for a .json template, does not find one for
     ?view=rank, and answers 404 with an empty body. Measured both ways — 404
     and 0 bytes with the header, 200 and the full payload without. (/search is
     not like this: ?view=terms answers either way, which is why the typo
     vocabulary in redesign-search.js gets away with sending it.) */
  function ensureIndex(then) {
    if (index) { if (then) then(); return; }
    try {
      var raw = sessionStorage.getItem(INDEX_KEY);
      var cached = raw ? JSON.parse(raw) : null;
      if (cached && cached.products && cached.products.length) {
        index = cached;
        if (then) then();
        return;
      }
    } catch (e) { /* private mode, or a stale value — just refetch */ }

    if (!indexPending) {
      indexPending = fetch(INDEX_URL)
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (txt) {
          var d = null;
          try { d = JSON.parse(txt); } catch (e) { return; }
          if (!d || !d.products || !d.products.length) return;
          index = d;
          try { sessionStorage.setItem(INDEX_KEY, txt); } catch (e) {}
        })
        .catch(function () { /* index stays null; the API path still works */ });
    }
    indexPending.then(function () { if (then) then(); });
  }

  /* ------------------------------------------------ matching */

  function tokens(s) {
    var n = (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    return n ? n.split(' ') : [];
  }

  /* Every token of the query must PREFIX some token of the record. "hipp du"
     finds HiPP Dutch; "dutch hipp" finds it too, because order does not matter.
     Nothing fuzzier than that here — the results page carries the typo
     tolerance, and guessing inside a dropdown that redraws on every keystroke
     reads as noise. */
  function hits(rec, qs) {
    for (var i = 0; i < qs.length; i++) {
      var found = false;
      for (var j = 0; j < rec.length; j++) {
        if (rec[j].indexOf(qs[i]) === 0) { found = true; break; }
      }
      if (!found) return false;
    }
    return true;
  }

  /* Both lists are printed in the page's sort order, and the index is fetched
     with ?sort_by=best-selling — so the first N matches ARE the N best sellers
     and there is nothing to re-sort. */
  function localProducts(q) {
    if (!index) return null;
    var qs = tokens(q), out = [], i;
    if (!qs.length) return out;
    for (i = 0; i < index.products.length && out.length < LIMIT; i++) {
      var p = index.products[i];
      if (!p._t) p._t = tokens(p.t + ' ' + (p.k || ''));
      if (hits(p._t, qs)) out.push(p);
    }
    return out;
  }

  function localCollections(q) {
    if (!index || !index.collections) return null;
    var qs = tokens(q), out = [], i;
    if (!qs.length) return out;
    for (i = 0; i < index.collections.length && out.length < DOCS; i++) {
      var c = index.collections[i];
      if (!c._t) c._t = tokens(c.t);
      if (hits(c._t, qs)) out.push(c);
    }
    return out;
  }

  /* ------------------------------------------------ popular suggestions

     Shopify has no query-suggestion corpus — this is the one block of the old
     app's panel that nothing in the API can answer. It does not need one: the
     completions ARE the catalogue. For a match on the word being typed, the
     word that FOLLOWS it in that product's title is the refinement — `hipp`
     against "HiPP Dutch Stage 1" offers `hipp dutch`, against "HiPP Comfort
     Formula" offers `hipp comfort`. Products are walked in sales order, so the
     lines people actually buy suggest first.

     The stop list is what stands between this and four rows of `hipp formula`,
     `hipp milk`, `hipp organic` — words that every title carries and that
     therefore narrow nothing. Bare numbers go for the same reason: `hipp 1` is
     not a search anyone means. */

  var STOP = {
    formula: 1, formulas: 1, milk: 1, baby: 1, infant: 1, organic: 1,
    combiotic: 1, combiotik: 1, bio: 1, stage: 1, and: 1, the: 1, with: 1, for: 1,
    of: 1, to: 1, a: 1, s: 1
  };

  function suggestions(q, limit) {
    if (!index) return [];
    var qs = tokens(q);
    if (!qs.length) return [];
    var last = qs[qs.length - 1];
    var head = qs.slice(0, qs.length - 1);
    var bag = Object.create(null), order = [];
    var pools = [index.products, index.collections || []];

    for (var s = 0; s < pools.length; s++) {
      var pool = pools[s];
      for (var i = 0; i < pool.length; i++) {
        var rec = pool[i];
        if (!rec._t) rec._t = tokens(rec.t + ' ' + (rec.k || ''));
        if (!hits(rec._t, qs)) continue;
        if (!rec._w) rec._w = tokens(rec.t);
        var w = rec._w, k;

        /* The word this record completes the half-typed token with — `hi` and
           `hipp` both resolve to `hipp`, so the phrase reads as a whole search
           rather than as the fragment plus a word. */
        var done = null, at = -1;
        for (k = 0; k < w.length; k++) { if (w[k].indexOf(last) === 0) { done = w[k]; at = k; break; } }
        if (!done) continue;

        for (k = 0; k < w.length; k++) {
          var nxt = w[k];
          if (nxt === done || STOP[nxt] || /^[0-9]+$/.test(nxt)) continue;
          if (qs.indexOf(nxt) > -1) continue;
          if (nxt.length < 2) continue;
          if (!bag[nxt]) {
            /* Which side of the typed word it sits on in the title. Typing
               `goat` and being offered `goat holle` reads backwards; the brand
               comes first in "Holle Goat Milk", so the suggestion should too.
               Only for a one-word query — past that, appending is the only
               placement that stays predictable. */
            bag[nxt] = { n: 0, done: done, before: k < at && head.length === 0 };
            order.push(nxt);
          }
          bag[nxt].n++;
        }
      }
    }

    /* Ranked by HOW MANY matching products carry the word, not by adjacency.
       Adjacency alone gave `hipp anti` — the front half of "Anti Reflux",
       dangling — while missing `hipp goat` entirely, because goat sits third in
       "HiPP Dutch Goat Stage 1". Frequency picks the words that actually split
       the range: a term on eight products is a fork in the road, a term on one
       is that product, which the row below already offers. Ties keep the order
       they were found in, which is sales order. */
    order.sort(function (a, b) { return bag[b].n - bag[a].n; });

    var out = [];
    for (var j = 0; j < order.length && out.length < limit; j++) {
      var word = order[j];
      if (bag[word].n < 2 && order.length > limit) continue;
      var b = bag[word];
      out.push(b.before
        ? { phrase: word + ' ' + b.done, typed: '', add: word, tail: ' ' + b.done }
        : { phrase: head.concat([b.done, word]).join(' '), typed: head.concat([b.done]).join(' '), add: word, tail: '' });
    }
    return out;
  }

  /* ------------------------------------------------ did you mean

     The dropdown's matching is prefix-only on purpose (see hits). What it
     could not do was answer a misspelling at all: "kendmil" drew "No matches"
     and nothing else, while the results page — one submit later — would have
     offered the correction. Same Damerau-Levenshtein pass as
     redesign-search.js, against the index's own vocabulary, and only when a
     query matched nothing locally. The two copies are deliberate: that file
     loads before this one on /search and only there, so neither can lean on
     the other. */

  function within(a, b, budget) {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > budget) return budget + 1;
    var prev2 = [], prev = [], cur = [], i, j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      cur[0] = i;
      var best = cur[0];
      for (j = 1; j <= b.length; j++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        var v = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 && a.charCodeAt(i - 1) === b.charCodeAt(j - 2) && a.charCodeAt(i - 2) === b.charCodeAt(j - 1)) {
          v = Math.min(v, prev2[j - 2] + 1);
        }
        cur[j] = v;
        if (v < best) best = v;
      }
      if (best > budget) return budget + 1;
      prev2 = prev.slice(); prev = cur.slice();
    }
    return prev[b.length];
  }
  function budgetFor(len) { return len <= 3 ? 0 : (len <= 5 ? 1 : 2); }

  var vocab = null;
  function vocabulary() {
    if (vocab || !index) return vocab;
    var set = Object.create(null);
    (index.products || []).forEach(function (p) { tokens(p.t + ' ' + (p.k || '')).forEach(function (w) { if (w.length > 2) set[w] = 1; }); });
    (index.collections || []).forEach(function (c) { tokens(c.t).forEach(function (w) { if (w.length > 2) set[w] = 1; }); });
    vocab = Object.keys(set);
    return vocab;
  }
  function correct(q) {
    var words = vocabulary();
    if (!words || !words.length) return '';
    var qs = tokens(q), changed = false;
    var fixed = qs.map(function (t) {
      if (words.indexOf(t) >= 0) return t;
      var budget = budgetFor(t.length);
      if (!budget) return t;
      var bestWord = null, bestDist = budget + 1;
      for (var i = 0; i < words.length; i++) {
        var d = within(t, words[i], budget);
        if (d < bestDist) { bestDist = d; bestWord = words[i]; if (d === 1) break; }
      }
      if (bestWord && bestDist <= budget) { changed = true; return bestWord; }
      return t;
    });
    return changed ? fixed.join(' ') : '';
  }

  /* ------------------------------------------------ recent searches

     The old panel opened with the visitor's last few searches; Shopify has
     no such memory, so the field keeps its own — six queries, newest first,
     in localStorage, written on every submit and shown when the field is
     focused with fewer than MIN characters in it. Nothing is stored until a
     search is actually run. */

  var RECENT_KEY = 'rd-recent-searches';
  function recents() {
    try { var r = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); return Array.isArray(r) ? r.filter(function (x) { return typeof x === 'string' && x; }).slice(0, 6) : []; }
    catch (e) { return []; }
  }
  function remember(q) {
    q = (q || '').trim();
    if (!q) return;
    var list = recents().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); });
    list.unshift(q);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 6))); } catch (e) {}
  }
  function forget() { try { localStorage.removeItem(RECENT_KEY); } catch (e) {} }

  /* ------------------------------------------------ shared helpers */

  function searchUrl(q) {
    return '/search?q=' + encodeURIComponent(q) + '&type=product,page,article&options%5Bprefix%5D=last';
  }

  /* The index stores prices as numbers already divided by 100 — the suggest
     API's decimal strings and Liquid's cent integers are both normalised in the
     template. Formatting matches the product card's money_without_trailing_zeros,
     which is what the same product shows in the grid below the panel: $34, not
     $34.00, and $45.49 kept in full. rdMoney (assets/redesign.js) does it in
     the session's currency and groups thousands the way the card does; the
     fallback is the old dollar format, for a page where it failed to load. */
  function money(v) {
    var n = parseFloat(v);
    if (!isFinite(n) || n <= 0) return '';
    if (window.rdMoney) return window.rdMoney.format(Math.round(n * 100), { trailingZeros: false });
    return '$' + (n % 1 === 0 ? String(n) : n.toFixed(2));
  }

  var pageAnswers = Object.create(null);

  /* ------------------------------------------------ per-field */

  function init(root, idx) {
    var input = root.querySelector('[data-rd-search-input]');
    var form = root.querySelector('form');
    if (!input || !form) return;

    var panel, items = [], active = -1, seq = 0, lastQ = '';

    /* -------------------------------------------- take the submit back */

    input.name = 'rd-q';
    var reassert = setInterval(function () { if (input.name !== 'rd-q') input.name = 'rd-q'; }, 400);
    setTimeout(function () { clearInterval(reassert); }, 6000);

    document.addEventListener('submit', function (e) {
      if (e.target !== form) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      go(input.value);
    }, true);

    document.addEventListener('keydown', function (e) {
      if (e.target !== input || e.key !== 'Enter') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (active >= 0 && items[active]) window.location.assign(items[active].href);
      else go(input.value);
    }, true);

    function go(q) {
      q = (q || '').trim();
      if (!q) return;
      remember(q);
      window.location.assign(searchUrl(q));
    }

    /* -------------------------------------------- panel */

    function ensurePanel() {
      if (panel) return panel;
      panel = document.createElement('div');
      panel.className = 'rd-pred';
      panel.id = 'rd-pred-panel-' + idx;
      panel.setAttribute('role', 'listbox');
      panel.hidden = true;
      root.appendChild(panel);

      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-controls', panel.id);
      input.setAttribute('aria-autocomplete', 'list');
      return panel;
    }

    function close() {
      if (!panel) return;
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      items = [];
      active = -1;
    }

    function open() {
      ensurePanel().hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }

    function group(title) {
      var h = document.createElement('div');
      h.className = 'rd-pred__group';
      h.textContent = title;
      return h;
    }

    function row(href, cls) {
      var a = document.createElement('a');
      a.className = 'rd-pred__row ' + cls;
      a.href = href;
      a.setAttribute('role', 'option');
      a.id = 'rd-pred-opt-' + idx + '-' + items.length;
      items.push(a);
      return a;
    }

    /* The review line, built the way redesign-product-card builds it: five
       glyphs and a count. A rating below 5 draws the run twice and clips the
       green copy to the score — 72 of the 73 products average 5.0, so all but
       one render identically to the plain run. */
    function reviewLine(v) {
      if (!v || !v[1]) return null;
      var avg = parseFloat(v[0]);
      var wrap = document.createElement('span');
      wrap.className = 'rd-pred__rev';

      var stars = document.createElement('span');
      stars.className = 'rd-stars';
      stars.textContent = '★★★★★';
      if (isFinite(avg) && avg < 5) {
        stars.className += ' rd-stars--frac';
        var fill = document.createElement('span');
        fill.textContent = '★★★★★';
        fill.style.width = Math.max(0, Math.min(100, (avg / 5) * 100)) + '%';
        stars.appendChild(fill);
      }
      stars.setAttribute('aria-hidden', 'true');
      wrap.appendChild(stars);
      wrap.appendChild(document.createTextNode(v[1] + ' Reviews'));
      return wrap;
    }

    function productRow(p) {
      var a = row(p.u, 'rd-pred__row--product');
      if (p.i) {
        var img = document.createElement('img');
        img.className = 'rd-pred__img';
        img.src = p.i;
        img.alt = '';
        img.width = 44; img.height = 44;
        img.loading = 'lazy';
        a.appendChild(img);
      }
      var box = document.createElement('span');
      box.className = 'rd-pred__meta';

      var t = document.createElement('span');
      t.className = 'rd-pred__title';
      t.textContent = p.t;
      box.appendChild(t);

      var rev = reviewLine(p.v);
      if (rev) box.appendChild(rev);

      var priced = money(p.p);
      if (priced) {
        var pz = document.createElement('span');
        pz.className = 'rd-pred__price';
        pz.textContent = priced;
        var was = money(p.c);
        if (was && parseFloat(p.c) > parseFloat(p.p)) {
          var s = document.createElement('s');
          s.textContent = was;
          pz.appendChild(document.createTextNode(' '));
          pz.appendChild(s);
        }
        /* Only the bad news is worth a badge. The app pinned "In Stock" on
           every row, which says nothing when almost everything is. */
        if (p.a === false) {
          var out = document.createElement('span');
          out.className = 'rd-pred__out';
          /* The site's own word for it — the card under this panel says
             "Priority Waitlist" and that is what the button on the product
             does. "Sold out" reads as a dead end; this one is an action. */
          out.textContent = 'Waitlist';
          pz.appendChild(out);
        }
        box.appendChild(pz);
      }
      a.appendChild(box);
      return a;
    }

    function textRow(d) {
      var a = row(d.u || d.url, 'rd-pred__row--text');
      a.textContent = d.t || d.title;
      return a;
    }

    /* -------------------------------------------- drawing

       ORDER: products, then categories, then pages. Sam's call, and the right
       one for this catalogue — someone typing `hipp` wants a tin, and the
       categories under it are the way to widen out, not the way in. */

    function draw(model, q) {
      var p = ensurePanel();
      p.textContent = '';
      items = [];
      active = -1;

      var any = model.products.length || model.collections.length || model.pages.length ||
                (model.sugs && model.sugs.length);

      /* The correction comes FIRST, whenever nothing in the catalogue matched
         locally — even if Shopify's page search went on to fuzzy-match a few
         articles. It used to live only inside the empty state, so the moment
         "kendmil" pulled up two Kendamil guides the row vanished and the
         panel offered articles instead of the tin someone was typing for. */
      if (model.dym) {
        var dym = row(searchUrl(model.dym), 'rd-pred__dym');
        dym.appendChild(document.createTextNode('Did you mean '));
        var b = document.createElement('b');
        b.textContent = model.dym;
        dym.appendChild(b);
        dym.appendChild(document.createTextNode('?'));
        p.appendChild(dym);
      }

      if (!any) {
        var none = document.createElement('p');
        none.className = 'rd-pred__none';
        none.textContent = 'No matches for “' + q + '”';
        p.appendChild(none);
        open();
        return;
      }

      /* Above the results, as the old panel had them: they are refinements of
         what is still being typed, not answers to it, so they are read before
         the eye commits to a row. A wrapped line of chips rather than a stacked
         list — the panel has no vertical room to spare. */
      if (model.sugs && model.sugs.length) {
        var strip = document.createElement('div');
        strip.className = 'rd-pred__sugs';
        /* The label rides ON the row rather than above it. A group heading of
           its own cost 30px of a panel that had only just stopped scrolling,
           and this block is four short chips — it does not need the runway the
           result groups do. */
        var lab = document.createElement('span');
        lab.className = 'rd-pred__sugslabel';
        lab.textContent = 'Popular';
        strip.appendChild(lab);
        model.sugs.forEach(function (sg) {
          var a = row(searchUrl(sg.phrase), 'rd-pred__sug');
          if (sg.typed) a.appendChild(document.createTextNode(sg.typed + ' '));
          var em = document.createElement('b');
          em.textContent = sg.add;
          a.appendChild(em);
          if (sg.tail) a.appendChild(document.createTextNode(sg.tail));
          strip.appendChild(a);
        });
        p.appendChild(strip);
      }

      if (model.products.length) {
        p.appendChild(group('Products'));
        model.products.forEach(function (pr) { p.appendChild(productRow(pr)); });
      }
      if (model.collections.length) {
        p.appendChild(group('Categories'));
        model.collections.forEach(function (c) { p.appendChild(textRow(c)); });
      }
      if (model.pages.length) {
        p.appendChild(group('Pages & articles'));
        model.pages.forEach(function (d) { p.appendChild(textRow(d)); });
      }

      var all = row(searchUrl(q), 'rd-pred__all');
      all.textContent = 'View all results for “' + q + '”';
      p.appendChild(all);

      open();
    }

    /* -------------------------------------------- fetching

       Pages and articles only. They arrive after the panel is already up and
       are drawn in underneath it, so nothing the reader is reading moves. */

    function fetchPages(q, andRedraw) {
      if (pageAnswers[q]) { andRedraw(pageAnswers[q]); return; }
      var mine = ++seq;
      var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
                '&resources[type]=page,article' +
                '&resources[limit]=' + DOCS +
                '&resources[limit_scope]=each';
      fetch(url, { headers: { 'Accept': 'application/json' } })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          var r = (data && data.resources && data.resources.results) || {};
          var docs = (r.pages || []).concat(r.articles || []).slice(0, DOCS);
          pageAnswers[q] = docs;
          if (mine === seq && lastQ === q) andRedraw(docs);
        })
        .catch(function () { /* the panel keeps what it has */ });
    }

    /* The fallback for the first keystrokes of a cold tab, before the index has
       landed: everything from the API in one call. resources[limit] is a TOTAL
       budget across the types unless limit_scope says otherwise — four types at
       limit 4 returns one of each, which is how a search for `hipp` on a store
       with 22 matching formulas once offered a single product. */
    function apiAll(q) {
      var mine = ++seq;
      var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
                '&resources[type]=product,collection,page,article' +
                /* the larger of the two, then each list is sliced to its own */
                '&resources[limit]=' + Math.max(LIMIT, DOCS) +
                '&resources[limit_scope]=each' +
                '&resources[options][unavailable_products]=last';
      fetch(url, { headers: { 'Accept': 'application/json' } })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          if (!data || mine !== seq || lastQ !== q) return;
          var r = (data.resources && data.resources.results) || {};
          draw({
            sugs: [],
            products: (r.products || []).slice(0, LIMIT).map(function (x) {
              return { u: x.url, t: x.title, i: x.featured_image && x.featured_image.url, p: x.price, c: x.compare_at_price_min, a: x.available };
            }),
            collections: (r.collections || []).slice(0, DOCS).map(function (c) { return { t: c.title, u: c.url }; }),
            pages: (r.pages || []).concat(r.articles || []).slice(0, DOCS).map(function (d) { return { t: d.title, u: d.url }; })
          }, q);
        })
        .catch(function () {});
    }

    function run(q) {
      if (!index) { apiAll(q); return; }
      var model = {
        sugs: suggestions(q, 4),
        products: localProducts(q) || [],
        collections: localCollections(q) || [],
        pages: pageAnswers[q] || []
      };
      if (!model.products.length && !model.collections.length && !model.sugs.length) {
        model.dym = correct(q);
      }
      draw(model, q);
      if (!pageAnswers[q]) {
        fetchPages(q, function (docs) {
          if (lastQ !== q) return;
          model.pages = docs;
          draw(model, q);
        });
      }
    }

    /* -------------------------------------------- input */

    /* Recent searches, when there is nothing typed yet. Chips, like the
       popular suggestions, plus a clear link; each chip is a real link to the
       results page so it works from the keyboard like any other row. */
    function drawRecents() {
      var list = recents();
      if (!list.length) { close(); return false; }
      var p = ensurePanel();
      p.textContent = '';
      items = []; active = -1;
      var strip = document.createElement('div');
      strip.className = 'rd-pred__sugs rd-pred__recent';
      var lab = document.createElement('span');
      lab.className = 'rd-pred__sugslabel';
      lab.textContent = 'Recent';
      strip.appendChild(lab);
      list.forEach(function (q) {
        var a = row(searchUrl(q), 'rd-pred__sug');
        a.textContent = q;
        a.addEventListener('click', function () { remember(q); });
        strip.appendChild(a);
      });
      var clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'rd-pred__clear';
      clear.textContent = 'Clear';
      clear.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); forget(); close(); });
      strip.appendChild(clear);
      p.appendChild(strip);
      open();
      return true;
    }

    var timer;
    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(timer);
      if (q.length < MIN) { lastQ = ''; drawRecents(); return; }
      if (q === lastQ) return;
      lastQ = q;
      /* Focus is the usual trigger, but it does not fire when the caret is
         already in the field — a restored page, an autofocused form, or a
         click that beat the deferred script. Asking again here costs nothing:
         ensureIndex dedupes on its own pending promise. */
      ensureIndex(function () { if (lastQ === q && index) run(q); });
      /* No debounce on the local path: matching 73 records is free, so the
         panel keeps up with the keyboard. The API fallback still gets one, and
         the pages request is deduped per query. */
      if (index) { run(q); return; }
      timer = setTimeout(function () { run(q); }, 90);
    });

    /* -------------------------------------------- keyboard */

    function setActive(i) {
      if (!items.length) return;
      if (active >= 0 && items[active]) items[active].classList.remove('is-active');
      active = (i + items.length) % items.length;
      items[active].classList.add('is-active');
      input.setAttribute('aria-activedescendant', items[active].id);
      items[active].scrollIntoView({ block: 'nearest' });
    }

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { close(); return; }
      if (!panel || panel.hidden || !items.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
    });

    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) close();
    });

    /* Fetched on focus, not on page load: a visitor who never opens search never
       pays for it, and by the second keystroke it is almost always there. */
    if (document.activeElement === input) ensureIndex();

    input.addEventListener('focus', function () {
      ensureIndex(function () {
        if (lastQ && panel && !panel.hidden) run(lastQ);
      });
      if (input.value.trim().length >= MIN) { if (panel && panel.textContent) open(); }
      else drawRecents();
    });
  }

  Array.prototype.forEach.call(document.querySelectorAll('[data-rd-search]'), init);
})();
